// afeBudgetRemote — reads and writes AFE budgets against the persistence tier
// (`apps/compute-server/blueprints/afe_store.py`).
//
// CHARTER NOTE: transport and boundary shape-check only. No Engineering / Economic /
// Statistical math — the roll-up is POST /api/afe/rollup in canonical Python, and this
// file must never gain a sum.
//
// WHY NOT RTK QUERY HERE. `afeApi.ts` declares these endpoints and stays the contract of
// record for the component layer; `afeBudgetRequests` there is tested as that contract.
// But `useAfeBudgetData` is consumed by hooks and by `seedAfeDemo` — a plain module, not
// a component — and its reads are SYNCHRONOUS across seven consumers. Routing it through
// RTK Query would make every read async and drag a Redux Provider into test suites that
// deliberately have none. Same endpoints, same shapes; this is the store's own reader,
// exactly as `features/wells/store/corpusApi.ts` is the spine's.
//
// AND THE COST THAT COMES WITH THAT, WHICH THE SPINE PAID FOR ALREADY. Staying out of
// Redux means staying out of `apiSlice.prepareHeaders`, so a naive fetch here would send
// NO Authorization and no acting-organization — an unauthenticated path to the same
// routes the authenticated slices use, invisible because the Flask server checks neither
// today. `corpusApi.ts` documents that discovery. `lib/requestContext.authHeaders()` is
// how it is paid, and it is used below for the same reason.

import { authHeaders } from "../../../lib/requestContext";
import type { AfeBandPercentiles, AfeBudget } from "../types";

/** Vite proxies `/api` to the compute server in dev (vite.config.ts); prod sets VITE_API_URL. */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export class AfeBudgetRemoteError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    /** True when the server was unreachable at all, as opposed to answering with an
     *  error. The store falls back to localStorage on the first and NOT on the second:
     *  a 409 is the server working correctly and must not be papered over. */
    readonly unreachable = false,
  ) {
    super(message);
    this.name = "AfeBudgetRemoteError";
  }
}

interface BudgetEnvelope {
  success: boolean;
  budget: AfeBudget;
  etag: string | null;
}

/** Etag per afeId, held so a write can carry the precondition the read established.
 *  Module-level rather than per-hook: two components editing one AFE must present the
 *  same version, or the second silently defeats the check by having no etag to send. */
const etags = new Map<string, string>();

export function etagFor(afeId: string): string | undefined {
  return etags.get(afeId);
}

/** Cleared when a budget is dropped from the cache, so a stale version can never be
 *  presented as current after a reload of that AFE. */
export function forgetEtag(afeId: string): void {
  etags.delete(afeId);
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        ...authHeaders(),
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(init?.headers ?? {}),
      },
    });
  } catch (e) {
    // Name the setup fault rather than surfacing a bare "Failed to fetch". This is the
    // one case the store may fall back to localStorage for.
    throw new AfeBudgetRemoteError(
      `Cannot reach the AFE API at ${API_BASE}${path}. Is the compute server running? ` +
        `(${(e as Error).message})`,
      undefined,
      true,
    );
  }
}

async function errorFrom(res: Response, fallback: string): Promise<string> {
  // PREFER THE SERVER'S OWN MESSAGE. It knows which store and which cause; a canned
  // string here would be the double-naming defect 0c0fdc7b removed, in reverse.
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error) return body.error;
  } catch {
    /* non-JSON body — fall through */
  }
  return fallback;
}

/** Register the AFE HEADER on the server, so its budget has a parent to attach to.
 *
 *  SEV-0 THIS CLOSES (adversarial review 2026-09-07). The budget moved to Postgres and the
 *  register did not: `useAfeData.createAfe` wrote only localStorage, nothing called
 *  POST /api/afes, so every budget write for an app-minted AFE hit a 404. A 404 is an
 *  ANSWER, so the store correctly refused to fall back — and `withRegisterRollback` then
 *  unwound the create. The app worked only while the server was down. It was masked in
 *  hands-on checks because the four migrated AFEs already had rows.
 *
 *  NOT FIXED SERVER-SIDE, deliberately. Upserting the header inside `put_budget` would
 *  reopen the forgeable-status hole that `create_afe`'s server-assigned `status='draft'`
 *  exists to close — a client could then mint an approved AFE through a budget write.
 *
 *  Sends `id` as the ClientRef the server requires. The declared `CreateAfeDTO` omits it,
 *  which is why `afeApi.createAfe` would have 400'd had anything called it.
 *
 *  Returns SILENTLY on an unreachable server: the caller continues into the localStorage
 *  path, which is the same unreachable-vs-answered policy the budget write uses. A server
 *  that ANSWERS with an error throws, because that is a refusal and must not be masked. */
export async function createAfeRemote(afe: {
  id: string;
  name: string;
  afeType: string;
  [k: string]: unknown;
}): Promise<void> {
  let res: Response;
  try {
    res = await request("/afes", {
      method: "POST",
      body: JSON.stringify(afe),
    });
  } catch (e) {
    if (e instanceof AfeBudgetRemoteError && e.unreachable) return;
    throw e;
  }

  // Already registered — the migrator inserted it, or a retry raced. Idempotent, not an
  // error: the parent exists, which is all the budget write needs.
  if (res.status === 409) return;

  if (!res.ok) {
    throw new AfeBudgetRemoteError(
      await errorFrom(res, `Could not register AFE ${afe.id} on the server.`),
      res.status,
    );
  }
}

/** One AFE's authored budget. Returns null when the AFE has no budget yet, which is
 *  different from a failure and must not be conflated with one. */
export async function fetchBudget(afeId: string): Promise<AfeBudget | null> {
  const res = await request(`/afes/${encodeURIComponent(afeId)}/budget`);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new AfeBudgetRemoteError(
      await errorFrom(res, `Could not load the budget for ${afeId}.`),
      res.status,
    );
  }
  const body = (await res.json()) as BudgetEnvelope;
  if (body.etag) etags.set(afeId, body.etag);
  return body.budget ?? null;
}

/** Whole-container replace. Returns nothing on success and THROWS on failure — the
 *  autosave in useAfeBuilder observes that rejection, which is the entire point of
 *  hardening it first. A silent failure here is AFE-15 F4 with a network in the middle.
 */
export async function writeBudget(budget: AfeBudget): Promise<void> {
  const etag = etags.get(budget.afeId);
  const res = await request(
    `/afes/${encodeURIComponent(budget.afeId)}/budget`,
    {
      method: "PUT",
      // Omitted on a first write: a precondition against a version that does not exist
      // would make every budget's first save a conflict.
      ...(etag ? { headers: { "If-Match": etag } } : {}),
      body: JSON.stringify({
        scope: budget.scope,
        lines: budget.lines,
      }),
    },
  );

  if (res.status === 409) {
    // Someone else wrote since this client read. Drop the etag so the next read
    // re-establishes one; retrying with the stale version would just conflict again.
    forgetEtag(budget.afeId);
    throw new AfeBudgetRemoteError(
      "This AFE was changed elsewhere since you loaded it. Reload to see the current " +
        "version before saving — your lines are still here and have NOT been saved.",
      409,
    );
  }
  if (!res.ok) {
    throw new AfeBudgetRemoteError(
      await errorFrom(res, `Could not save the budget for ${budget.afeId}.`),
      res.status,
    );
  }

  const body = (await res.json()) as { etag?: string };
  if (body.etag) etags.set(budget.afeId, body.etag);
}

interface PercentilesEnvelope {
  success: boolean;
  afe?: { etag?: string | null; percentiles?: AfeBandPercentiles };
  changed?: boolean;
}

/** Change what the band slots mean on one AFE (#65). Python validates the set and logs the change;
 *  this only carries it. Sends the AFE's current etag — the server REQUIRES one here (428 without,
 *  409 when stale) — and stores the new etag on success, so the Builder's next autosave presents
 *  the version this change created instead of tripping a 409 on the user's own edit. When no read
 *  has established an etag yet, one budget read establishes it first.
 *
 *  Throws on any failure. Unlike the budget write there is NO localStorage fallback: a percentile
 *  set that never reached the server must not look applied. */
export async function setAfePercentilesRemote(
  afeId: string,
  percentiles: AfeBandPercentiles,
): Promise<AfeBandPercentiles> {
  if (!etags.has(afeId)) await fetchBudget(afeId);
  const sent = etags.get(afeId);
  const res = await request(`/afes/${encodeURIComponent(afeId)}/percentiles`, {
    method: "POST",
    // If-Match is the precondition G10 asks for; the server also reads a body etag.
    ...(sent ? { headers: { "If-Match": sent } } : {}),
    body: JSON.stringify({ percentiles }),
  });
  if (!res.ok) {
    // A 409 does NOT touch the etag map. Another save (an autosave, another tab) moved the AFE
    // on, and whichever write did that already stored its own etag; deleting it here would leave
    // the next autosave with none and lock it out with 428s. Nor does it re-read to "try again":
    // that would launder the conflict and let this tab's stale lines overwrite the other write.
    throw new AfeBudgetRemoteError(
      res.status === 409
        ? "This AFE changed since you loaded it (another save or another tab), so the percentiles were not changed. Reload the page, then set them again."
        : await errorFrom(
            res,
            `Could not change the percentiles for ${afeId}.`,
          ),
      res.status,
    );
  }
  const body = (await res.json()) as PercentilesEnvelope;
  if (body.afe?.etag) etags.set(afeId, body.afe.etag);
  if (!body.afe?.percentiles) {
    throw new AfeBudgetRemoteError(
      `The server did not return percentiles for ${afeId}.`,
      res.status,
    );
  }
  return body.afe.percentiles;
}
